/**
 * @file Phone authentication standalone component.
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Alert from '../global/Alert';
import Button from '../global/Button';
import PhoneInput from '../global/PhoneInput';
import FormGroup from '../global/FormGroup';
import BoxedArrowLink from '../global/BoxedArrowLink';
import Spinner from '../global/Spinner';
import { formatPhone, parsePhone } from '../../util/phone';
import getLogger from '../../util/logger';
import { AUTH_TYPE } from '../../constants/auth';
import { ACCOUNT_REFERRAL_TYPE } from '../../constants/account-referrals';

const log = getLogger('PhoneAuth');
const INACTIVITY_TIMEOUT = 20000; // 20s

class PhoneAuth extends Component {
  static propTypes = {
    loading: PropTypes.bool,
    error: PropTypes.string,
    phoneAuthSubmit: PropTypes.func.isRequired,
    phoneAuthVerify: PropTypes.func.isRequired,
    resendPhoneAuthCode: PropTypes.func.isRequired,
    className: PropTypes.string,
    introText: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.string,
    ]),
    verificationText: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.string,
    ]),
    onLogin: PropTypes.func,
    children: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node),
    ]),
    submitText: PropTypes.string,
    submitClassName: PropTypes.string,
    pinSubmitText: PropTypes.string,
    defaultPhone: PropTypes.string,
    onEmailAuth: PropTypes.func,
    accountReferralCreate: PropTypes.func,
    enableEmailFallback: PropTypes.bool,
  };

  static defaultProps = {
    introText:
      'Verify your mobile number to enter. Waldo will text a verification code to the number below.',
    submitText: 'Submit',
    verificationText: 'Please enter the code that was sent to',
    onLogin: () => {},
    enableEmailFallback: true,
  };

  state = {
    phone: this.props.defaultPhone || null,
    verificationCode: '',
    phoneSubmitted: false,
    showChildrenOnPinEntry: false,
    isUSPhoneNumber: true, // Default to true.
  };

  onPhoneInputChange = phone => {
    this.setState(() => ({ phone }));
  };

  onVerificationCodeChange = event => {
    const { value } = event.target;
    this.setState(() => ({
      verificationCode: value,
    }));
  };

  onSubmitPhoneAuthRequest = async event => {
    event.preventDefault();

    const { phone } = this.state;
    const { phoneAuthSubmit } = this.props;
    try {
      await phoneAuthSubmit(phone, true);
      this.setState(() => ({
        phoneSubmitted: true,
      }));
      setTimeout(() => {
        // Show email link after 20s if user still here.
        this.setState(() => ({
          showChildrenOnPinEntry: true,
        }));
      }, INACTIVITY_TIMEOUT);
    } catch (error) {
      log.error('onSubmitPhoneAuthRequest() :: Error:', error);
    }
  };

  onSubmitPhoneVerify = async event => {
    event.preventDefault();

    const { phone, verificationCode } = this.state;
    const { phoneAuthVerify, onLogin, accountReferralCreate } = this.props;

    await phoneAuthVerify(parsePhone(phone), verificationCode, async () => {
      await accountReferralCreate(ACCOUNT_REFERRAL_TYPE.ACCOUNT_SIGNUP);
      onLogin(AUTH_TYPE.PHONE);
    });
  };

  onResendVerificationCode = async () => {
    const { phone } = this.state;
    const { resendPhoneAuthCode } = this.props;
    await resendPhoneAuthCode(phone);
  };

  countryDialCodeChange = countryDialCode =>
    this.setState(() => ({ isUSPhoneNumber: countryDialCode === '+1' }));

  render() {
    const {
      phone,
      phoneSubmitted,
      showChildrenOnPinEntry,
      isUSPhoneNumber,
    } = this.state;

    const {
      loading,
      error,
      className,
      introText,
      verificationText,
      children,
      submitText,
      submitClassName,
      pinSubmitText = 'Submit',
      onEmailAuth,
      enableEmailFallback,
    } = this.props;

    const submitClasses = classNames({
      'text-extra-bold': true,
      [submitClassName]: !!submitClassName,
    });

    const pinSubmitClasses = classNames({
      'mt-0': true,
      [submitClassName]: !!submitClassName,
    });

    return (
      <div className={className}>
        {error && <Alert type="danger">{error}</Alert>}

        {!phoneSubmitted ? (
          <form onSubmit={this.onSubmitPhoneAuthRequest} method="POST">
            <p>{introText}</p>

            <PhoneInput
              id="phone"
              label="Mobile Number"
              value={this.state.phone || '+1'}
              onChange={this.onPhoneInputChange}
              autoFocus
              className="mb-1"
              onPhoneCodeChange={this.countryDialCodeChange}
            />

            {!isUSPhoneNumber && onEmailAuth && enableEmailFallback && (
              <Alert type="info">
                Can&apos;t receive a US SMS message?{' '}
                <Button
                  btnStyle="link"
                  btnSize="sm"
                  className="non-us-signup-link"
                  onClick={onEmailAuth}
                >
                  Authenticate with email
                </Button>
              </Alert>
            )}

            <Button type="submit" disabled={loading} className={submitClasses}>
              {loading ? <Spinner size="xs" light /> : submitText}
            </Button>

            {children}
          </form>
        ) : (
          <form onSubmit={this.onSubmitPhoneVerify} method="POST">
            <p>
              {verificationText} {formatPhone(phone)}
            </p>

            <FormGroup
              id="phone-code"
              type="text"
              label="Pin"
              name="code"
              onChange={this.onVerificationCodeChange}
              required
            />

            <Button
              type="submit"
              className={pinSubmitClasses}
              disabled={loading || this.state.verificationCode.length < 4}
            >
              {loading ? <Spinner size="xs" light /> : pinSubmitText}
            </Button>

            <BoxedArrowLink
              onClick={this.onResendVerificationCode}
              className="mt-4"
            >
              Re-send code
            </BoxedArrowLink>
            {showChildrenOnPinEntry && (
              <Alert type="info" className="mt-2">
                Not receiving the SMS?{' '}
                <Button
                  btnStyle="link"
                  className="non-us-signup-link"
                  onClick={onEmailAuth}
                >
                  Authenticate with email
                </Button>
              </Alert>
            )}
            {showChildrenOnPinEntry && <Fragment>{children}</Fragment>}
          </form>
        )}
      </div>
    );
  }
}

export default PhoneAuth;
